import type { MonetizationAgentSummary } from '@monorepo/shared/schemas/analytics'
import { Activity, GitBranch, Target } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'

interface AgentPerformanceGridProps {
  agents: MonetizationAgentSummary[]
  loading?: boolean
  onViewAgent?: (agentId: string) => void
}

export function AgentPerformanceGrid({
  agents,
  loading = false,
  onViewAgent,
}: AgentPerformanceGridProps) {
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`
    }
    return num.toLocaleString()
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}k`
    }
    return `$${amount.toLocaleString()}`
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return {
          variant: 'default' as const,
          color: 'bg-green-100 text-green-800',
        }
      case 'draft':
        return {
          variant: 'secondary' as const,
          color: 'bg-gray-100 text-gray-800',
        }
      default:
        return {
          variant: 'outline' as const,
          color: 'bg-orange-100 text-orange-800',
        }
    }
  }

  if (loading) {
    return (
      <div>
        <h2 className='text-xl font-semibold mb-4'>Agents</h2>
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
          {Array.from({ length: 3 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
            <Card key={i} className='bg-card border'>
              <CardHeader>
                <div className='animate-pulse'>
                  <div className='flex justify-between items-start mb-2'>
                    <div className='h-4 bg-slate-300 dark:bg-slate-600 rounded w-16'></div>
                    <div className='h-8 w-8 bg-slate-300 dark:bg-slate-600 rounded'></div>
                  </div>
                  <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-32 mb-1'></div>
                </div>
              </CardHeader>
              <CardContent>
                <div className='animate-pulse space-y-4'>
                  <div className='grid grid-cols-3 gap-4'>
                    {Array.from({ length: 6 }).map((_, j) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      <div key={j} className='text-center space-y-1'>
                        <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-8 mx-auto'></div>
                        <div className='h-3 bg-slate-300 dark:bg-slate-600 rounded w-12 mx-auto'></div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (agents.length === 0) {
    return (
      <div>
        <h2 className='text-xl font-semibold mb-4'>Agents</h2>
        <p className='text-muted-foreground'>
          No agent activity found for this period.
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className='mb-4'>
        <h2 className='text-xl font-semibold'>Agents</h2>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
        {agents.map(agent => {
          const statusBadge = getStatusBadge(agent.status)

          return (
            <Card
              key={agent.agent_id}
              className='bg-card border hover:shadow-lg transition-all duration-200 cursor-pointer group'
              onClick={() => onViewAgent?.(agent.agent_id)}
            >
              <CardHeader className='pb-2'>
                <div className='flex justify-between items-center mb-2'>
                  <CardTitle className='text-lg font-semibold leading-tight'>
                    {agent.agent_name}
                  </CardTitle>
                  <Badge
                    variant={statusBadge.variant}
                    className={`text-xs ${statusBadge.color}`}
                  >
                    {agent.status}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className='space-y-4'>
                {/* Activity Metrics */}
                <div className='grid grid-cols-3 gap-3 text-center'>
                  <div>
                    <div className='text-lg font-semibold text-blue-600 flex items-center justify-center gap-1'>
                      <Activity className='w-4 h-4' />
                      {formatNumber(agent.task_runs)}
                    </div>
                    <div className='text-xs text-muted-foreground'>Tasks</div>
                  </div>
                  <div>
                    <div className='text-lg font-semibold text-purple-600 flex items-center justify-center gap-1'>
                      <GitBranch className='w-4 h-4' />
                      {formatNumber(agent.workflow_runs)}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Workflows
                    </div>
                  </div>
                  <div>
                    <div className='text-lg font-semibold text-green-600 flex items-center justify-center gap-1'>
                      <Target className='w-4 h-4' />
                      {formatNumber(agent.outcome_runs)}
                    </div>
                    <div className='text-xs text-muted-foreground'>
                      Outcomes
                    </div>
                  </div>
                </div>

                {/* Financial Metrics */}
                <div className='grid grid-cols-3 gap-3 text-center border-t pt-3'>
                  <div>
                    <div className='text-sm font-semibold text-red-500'>
                      {formatCurrency(agent.cost_usd)}
                    </div>
                    <div className='text-xs text-muted-foreground'>Cost</div>
                  </div>
                  <div>
                    <div className='text-sm font-semibold text-green-500'>
                      {formatCurrency(agent.revenue_usd)}
                    </div>
                    <div className='text-xs text-muted-foreground'>Revenue</div>
                  </div>
                  <div>
                    <div className='text-sm font-semibold text-purple-500'>
                      {agent.margin_percentage.toFixed(1)}%
                    </div>
                    <div className='text-xs text-muted-foreground'>Margin</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
